import Cookies from 'js-cookie';
import { getIdentityFromCookie, setLocalIdentity, setMemoryIdentity } from '@meitu/xdesign-identity';
import { message } from '@/ComUI';
import { getConfig } from '@/configProvider';
import { uploadFileToObs } from '@/uploadManager';
import { getDefaultObsConfig, getObsConfigAsync } from '@/uploadManager/config';
import { getRootDomain, getSuffix } from '@/utils';
import { setLocalUid } from '@/utils/uidKit';
import { createTeamApi, handleDownloadRecord } from '../api';
import { fetchAccessTokenByCode, fetchEnterpriseConfigs, fetchUserInfo, refreshUserRights } from '../api/enterprise';

export const getEnterpriseIDKey = () => {
    return `enterprise_id${getSuffix()}`;
};

export const getAccessTokenCookieKey = () => {
    return `access_token${getSuffix()}`;
};

// 是否走SSO授权登录方式
let isSSOLogin = false;
export const isSSO = () => isSSOLogin;

const getEnterpriseSearchParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const localOid = getIdentityFromCookie()?.oid;
    return {
        code: searchParams.get('code'),
        organizationID: Number(searchParams.get('oid')) || localOid,
    };
};

/**
 * Clear redundant url params
 * (remove some params from `meitu account`)
 * @param keys - keys to remove
 */
const clearURL = (keys?: string[]) => {
    const searchParams = new URLSearchParams(window.location.search);
    const redundantKeys = [
        'op_type',
        'code',
        'platform',
        'client_from_sdk',
        'redirect_uri',
        'client_referer',
        'client_params',
    ];
    if (keys) {
        redundantKeys.push(...keys);
    }

    redundantKeys.forEach((key) => {
        searchParams.delete(key);
    });

    // 构建新的查询字符串
    const newQueryString = searchParams.toString();

    // 获取当前 URL 的路径部分
    const path = window.location.pathname;

    // 更新 URL，不包括被移除的参数
    const newUrl = newQueryString ? `${path}?${newQueryString}` : path;
    window.history.replaceState('', '', newUrl);
};

const checkLoginStatus = async () => {
    try {
        const res: any = await fetchUserInfo();

        // Not login
        if (res?.userInfo?.id) {
            const uid = res.userInfo.id;
            // 非个人版下，利用该接口同步最新ouid，用于埋点上报的uid的准确性
            setLocalUid(uid);
            return true;
        }

        return false;
    } catch (e) {
        return false;
    }
};

const getEnterpriseConfigs = async (oid: string) => {
    try {
        const res: any = await fetchEnterpriseConfigs(oid);

        return {
            enterpriseID: res.enterprise,
            authPageURL: res.authorized_redirect_url,
        };
    } catch (_) {
        return null;
    }
};

export const init = async () => {
    const { code, organizationID } = getEnterpriseSearchParams();
    const { isWpsUser } = getConfig();

    let obsConfig = getDefaultObsConfig();

    const TOKEN_KEY_COOKIE = getAccessTokenCookieKey();

    console.log('zxcc teamInfo getEnterpriseSearchParams --code: ', code);
    console.log('zxcc teamInfo getEnterpriseSearchParams --organizationID: ', organizationID);

    // Check whether it is enterprise client
    if (!organizationID && !code) {
        // 同步cookie中的token到缓存
        const tokenCookie = Cookies.get(TOKEN_KEY_COOKIE) || '';
        if (tokenCookie && !isWpsUser) {
            window.localStorage.setItem('access_token', tokenCookie);
        }
        return obsConfig;
    }

    const ENTERPRISE_ID = getEnterpriseIDKey();

    // 更新本地cookie
    setLocalIdentity(Number(organizationID), Number(organizationID));
    // 更内存身份
    setMemoryIdentity(Number(organizationID), Number(organizationID));

    // If no `code` url param, then checking login status
    if (!code) {
        // 同步cookie中的token到缓存
        const tokenCookie = Cookies.get(TOKEN_KEY_COOKIE) || '';

        if (tokenCookie && !isWpsUser) {
            window.localStorage.setItem('access_token', tokenCookie);
        }

        // check login status
        try {
            const [isLogin, resObsCfg, resEnterpriseCfg] = await Promise.all([
                checkLoginStatus(),
                getObsConfigAsync({ oid: organizationID }),
                getEnterpriseConfigs(organizationID),
            ]);

            if (resObsCfg?.app) {
                obsConfig = {
                    ...obsConfig,
                    ...resObsCfg,
                };
            }

            isSSOLogin = !!resEnterpriseCfg?.authPageURL;

            if (!isLogin) {
                // not login, then redirect to auth page
                window.localStorage.removeItem('access_token');
                Cookies.remove(TOKEN_KEY_COOKIE, {
                    domain: getRootDomain(),
                });
            }

            if (resEnterpriseCfg?.enterpriseID) {
                // 更新企业标识
                Cookies.set(ENTERPRISE_ID, resEnterpriseCfg.enterpriseID, {
                    domain: getRootDomain(),
                    expires: 36500, // 100年有效期
                });
            } else {
                Cookies.remove(ENTERPRISE_ID, {
                    domain: getRootDomain(),
                });
            }

            if (!isLogin && resEnterpriseCfg?.authPageURL) {
                // 未登录，跳转去授权登录
                window.open(resEnterpriseCfg.authPageURL, '_self');
            }

            clearURL(['enterprise', 'oid']);

            return obsConfig;
        } catch (_) {
            return obsConfig;
        }
    }

    // Core flow
    try {
        // fetch access token by code
        const res: any = await fetchAccessTokenByCode(code);
        const accessToken = res['access_token'];
        if (!accessToken) {
            throw new Error('Get access token failed.');
        }

        if (!isWpsUser) {
            window.localStorage.setItem('access_token', accessToken);
        }
        Cookies.set(TOKEN_KEY_COOKIE, accessToken, {
            domain: getRootDomain(),
            expires: 36500, // 100年有效期
        });

        // refresh user rights
        const [_, resObsCfg, resEnterpriseCfg]: any = await Promise.all([
            refreshUserRights(),
            getObsConfigAsync({ oid: organizationID }),
            getEnterpriseConfigs(organizationID),
        ]);

        if (resObsCfg?.app) {
            obsConfig.app = resObsCfg?.app;
        }

        isSSOLogin = !!resEnterpriseCfg?.authPageURL;

        if (resEnterpriseCfg?.enterpriseID) {
            // 更新企业标识
            Cookies.set(ENTERPRISE_ID, resEnterpriseCfg.enterpriseID, {
                domain: getRootDomain(),
                expires: 36500, // 100年有效期
            });
        }

        return obsConfig;
    } catch (_) {
        // treat as not login
    } finally {
        clearURL(['enterprise', 'oid']);
    }

    return obsConfig;
};

// 同步海洛下载结果
export const handleReportDownloadResult = async (
    blob: Blob | string,
    options: {
        // 一级菜单： AI商拍   图像处理  AI设计
        menu: string;
        // 二级菜单
        sub_menu: string;
        // 图片宽
        width?: number;
        // 图片高
        height?: number;
        // 文件的扩展名  jpg png zip pptx
        extension: string;
        // 作图记录的标题
        title?: string;
    },
) => {
    try {
        let url = '';
        let filesize = undefined;
        if (typeof blob === 'string' && /^https?:\/\//i.test(blob)) {
            url = blob;
        } else if (blob instanceof Blob) {
            const file = new File([blob], '', {
                type: blob.type,
            });
            const res = await uploadFileToObs(file, {
                customMaatType: 'image_tmp',
                customSuffix: options?.extension,
            });

            url = res.url;

            filesize = blob.size;
        }

        await handleDownloadRecord({
            url,
            filesize,
            ...options,
        });
        message.success('已为您保存至海洛数字资产');
    } catch (e) {
        console.log(e);
    }
};

export const createTeam = async (params?: {
    /** 默认创建名称为"xxx的团队"的团队 */
    teamName?: string;
    rebateCode?: string;
}): Promise<{
    groupId: number;
    oid: number;
    ouid: number;
    /** 名称重复时后端会加随机数, 所以用接口返回的名称 */
    teamName: string;
}> => {
    const { userInfo } = getConfig();
    const res = await createTeamApi({
        name: params?.teamName ?? `${userInfo.screen_name}的团队`,
        rebate_code: params?.rebateCode,
    });
    return {
        groupId: res.group_id,
        oid: res.oid,
        ouid: res.ouid,
        teamName: res.team_name,
    };
};
